import React, { useEffect, useRef, useState } from 'react'

import { BookableCard } from './bookable-card'
import FilledArrow from '@Assets/icons/filled-arrow.svg'

const CLASS_NAME = 'bookable-selection-screen'

const SCROLL_PADDING = 48

type BookablesScrollContainerProps = {
  slots: Array<{ id: string; display: { localTime: string } }>
  onArrowVisibilityChange?: (showArrows: boolean) => void
  selectedSlotId?: string
}

export const BookablesScrollContainer = ({
  slots,
  onArrowVisibilityChange,
  selectedSlotId,
}: BookablesScrollContainerProps) => {
  const scrollContainerRef = useRef<HTMLElement>(null)
  const [showLeftArrow, setShowLeftArrow] = useState(false)
  const [showRightArrow, setShowRightArrow] = useState(false)

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) {
      return
    }

    let isDown = false
    let startX = 0
    let scrollLeft = 0
    let hasMoved = false

    const handleMouseDown = (e: MouseEvent) => {
      // Only handle left mouse button
      if (e.button !== 0) {
        return
      }

      isDown = true
      hasMoved = false
      scrollContainer.style.cursor = 'grabbing'
      scrollContainer.style.userSelect = 'none'
      startX = e.pageX - scrollContainer.offsetLeft
      scrollLeft = scrollContainer.scrollLeft
    }

    const handleMouseLeave = () => {
      if (!isDown) {
        return
      }
      isDown = false
      scrollContainer.style.cursor = 'grab'
      scrollContainer.style.userSelect = 'auto'
    }

    const handleMouseUp = () => {
      if (!isDown) {
        return
      }
      isDown = false
      scrollContainer.style.cursor = 'grab'
      scrollContainer.style.userSelect = 'auto'
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDown) {
        return
      }
      e.preventDefault()
      hasMoved = true
      const x = e.pageX - scrollContainer.offsetLeft
      const walk = (x - startX) * 2
      scrollContainer.scrollLeft = scrollLeft - walk
    }

    const handleClick = (e: MouseEvent) => {
      // Prevent clicks on child elements if we were dragging
      if (hasMoved) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    // Global mouseup to handle cases where mouse leaves window while dragging
    const handleGlobalMouseUp = () => {
      if (isDown) {
        isDown = false
        scrollContainer.style.cursor = 'grab'
        scrollContainer.style.userSelect = 'auto'
      }
    }

    scrollContainer.addEventListener('mousedown', handleMouseDown)
    scrollContainer.addEventListener('mouseleave', handleMouseLeave)
    scrollContainer.addEventListener('mouseup', handleMouseUp)
    scrollContainer.addEventListener('mousemove', handleMouseMove)
    scrollContainer.addEventListener('click', handleClick, true)
    window.addEventListener('mouseup', handleGlobalMouseUp)

    return () => {
      scrollContainer.removeEventListener('mousedown', handleMouseDown)
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave)
      scrollContainer.removeEventListener('mouseup', handleMouseUp)
      scrollContainer.removeEventListener('mousemove', handleMouseMove)
      scrollContainer.removeEventListener('click', handleClick, true)
      window.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [])

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) {
      return
    }

    const checkScrollPosition = () => {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainer
      const isAtStart = scrollLeft <= 0
      const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1

      const showLeft = !isAtStart && scrollWidth > clientWidth
      const showRight = !isAtEnd && scrollWidth > clientWidth

      setShowLeftArrow(showLeft)
      setShowRightArrow(showRight)

      onArrowVisibilityChange?.(showLeft || showRight)
    }

    checkScrollPosition()
    scrollContainer.addEventListener('scroll', checkScrollPosition)

    const resizeObserver = new ResizeObserver(checkScrollPosition)
    resizeObserver.observe(scrollContainer)

    return () => {
      scrollContainer.removeEventListener('scroll', checkScrollPosition)
      resizeObserver.disconnect()
    }
  }, [slots, onArrowVisibilityChange])

  const handleScrollLeft = () => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) {
      return
    }

    const scrollAmount = scrollContainer.clientWidth - SCROLL_PADDING
    scrollContainer.scrollBy({ left: -scrollAmount, behavior: 'smooth' })
  }

  const handleScrollRight = () => {
    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) {
      return
    }

    const scrollAmount = scrollContainer.clientWidth - SCROLL_PADDING
    scrollContainer.scrollBy({ left: scrollAmount, behavior: 'smooth' })
  }

  return (
    <div className={`${CLASS_NAME}__bookables-container`}>
      {showLeftArrow && (
        <button
          className={`${CLASS_NAME}__scroll-arrow ${CLASS_NAME}__scroll-arrow--left`}
          onClick={handleScrollLeft}
          aria-label="Scroll left">
          <img
            src={FilledArrow}
            alt=""
            aria-hidden="true"
            className={`${CLASS_NAME}__scroll-arrow-icon`}
          />
        </button>
      )}
      <section className={`${CLASS_NAME}__bookables`} ref={scrollContainerRef}>
        {slots.map((slot) => (
          <React.Fragment key={`fragment-${slot.id}`}>
            <BookableCard
              id={`${slot.id}`}
              key={`${slot.id}`}
              time={slot.display.localTime.toLowerCase().replace(/\s+/g, '')}
              isChecked={selectedSlotId === slot.id}
            />
          </React.Fragment>
        ))}
      </section>
      {showRightArrow && (
        <button
          className={`${CLASS_NAME}__scroll-arrow ${CLASS_NAME}__scroll-arrow--right`}
          onClick={handleScrollRight}
          aria-label="Scroll right">
          <img
            src={FilledArrow}
            alt=""
            aria-hidden="true"
            className={`${CLASS_NAME}__scroll-arrow-icon`}
          />
        </button>
      )}
    </div>
  )
}
